export interface ImportFilterAPI {
  field: string;
  operator: string;
  value: string;
}

export interface ImportFiltersUI {
  include: Record<string, string[]>;
  exclude: Record<string, string[]>;
}

const CONTAINS_FIELDS = ['tags', 'payment_method', 'shipping_method'];

/**
 * Maps the flat array filter format from the API to the nested include/exclude format used by the UI.
 */
export const mapFiltersToUI = (apiFilters: any | null | undefined): ImportFiltersUI => {
  const uiFilters: ImportFiltersUI = {
    include: {},
    exclude: {},
  };

  if (!apiFilters) return uiFilters;

  // Case 1: Legacy format (nested object { include: {...}, exclude: {...} })
  if (!Array.isArray(apiFilters) && typeof apiFilters === 'object') {
    if (apiFilters.include) {
      Object.entries(apiFilters.include).forEach(([field, values]) => {
        uiFilters.include[field] = Array.isArray(values) ? [...(values as string[])] : [];
      });
    }
    if (apiFilters.exclude) {
      Object.entries(apiFilters.exclude).forEach(([field, values]) => {
        uiFilters.exclude[field] = Array.isArray(values) ? [...(values as string[])] : [];
      });
    }
    return uiFilters;
  }

  // Case 2: New format (array of { field, operator, value })
  if (Array.isArray(apiFilters)) {
    apiFilters.forEach((filter) => {
      const { field, operator, value } = filter;
      const target = (operator === 'not_equals' || operator === 'not_contains') ? uiFilters.exclude : uiFilters.include;
      
      if (!target[field]) {
        target[field] = [];
      }
      target[field].push(value);
    });
  }

  return uiFilters;
};

/**
 * Maps the nested include/exclude UI structure back to the flat array format required by the API.
 */
export const mapFiltersToAPI = (uiFilters: ImportFiltersUI): ImportFilterAPI[] => {
  const apiFilters: ImportFilterAPI[] = [];

  // Map inclusions
  Object.entries(uiFilters.include).forEach(([field, values]) => {
    values.forEach((value) => {
      apiFilters.push({
        field,
        operator: CONTAINS_FIELDS.includes(field) ? 'contains' : 'equals',
        value,
      });
    });
  });

  // Map exclusions
  Object.entries(uiFilters.exclude).forEach(([field, values]) => {
    values.forEach((value) => {
      apiFilters.push({
        field,
        operator: CONTAINS_FIELDS.includes(field) ? 'not_contains' : 'not_equals',
        value,
      });
    });
  });

  return apiFilters;
};
